import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, Send } from "lucide-react";

export const EmailSettingsCard = () => {
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [sender, setSender] = useState("");
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState("");
  const [nudge, setNudge] = useState({ nudge_enabled: true, nudge_after_hours: 3 });

  const load = () => api.get("/admin/email-settings").then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/settings")
      .then(({ data }) => setNudge({
        nudge_enabled: data.nudge_enabled !== false,
        nudge_after_hours: data.nudge_after_hours || 3,
      }))
      .catch(() => {});
  }, []);

  const saveNudge = async (patch) => {
    const next = { ...nudge, ...patch };
    setNudge(next);
    try {
      await api.put("/settings", {
        nudge_enabled: next.nudge_enabled,
        nudge_after_hours: parseFloat(next.nudge_after_hours) || 3,
      });
      toast.success(next.nudge_enabled ? "Reminder emails are on" : "Reminder emails are off");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const save = async () => {
    setBusy(true);
    setLastError("");
    try {
      const { data } = await api.put("/admin/email-settings", { api_key: apiKey, sender });
      setStatus(data);
      setApiKey("");
      toast.success("Email settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testTo) return toast.error("Enter an email address to send the test to");
    setBusy(true);
    setLastError("");
    try {
      await api.post("/admin/email-settings/test", { to: testTo });
      toast.success(`Test email sent to ${testTo} — check the inbox (and spam)`);
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail);
      setLastError(detail);
      toast.error("Resend rejected the email — see the reason below");
    } finally { setBusy(false); }
  };

  if (!status) return null;

  return (
    <Card className="border-border" data-testid="email-settings-card">
      <CardContent className="p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 mb-1">
          <h3 className="font-display text-2xl flex items-center gap-2"><Mail size={18}/> Invoice Emails</h3>
          <Badge
            variant="outline"
            className={status.configured
              ? "border-primary/40 text-primary bg-primary/10"
              : "border-destructive/40 text-destructive bg-destructive/10"}
            data-testid="email-status-badge"
          >
            {status.configured ? `Resend active (${status.source})` : "Not configured"}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          {status.env_locked
            ? `Sending from ${status.sender} using the server environment key (····${status.api_key_last4}).`
            : "GST invoices are emailed through Resend. The sender's domain must be verified in your Resend dashboard (Domains → add DNS records) before Resend will deliver anything."}
        </p>

        {!status.env_locked && (
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="overline">Resend API Key {status.api_key_last4 && `(saved ····${status.api_key_last4})`}</Label>
              <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="re_xxxxxxxxxxxx" className="mt-2 bg-transparent" data-testid="email-api-key"/>
            </div>
            <div>
              <Label className="overline">Sender Address</Label>
              <Input value={sender} onChange={(e) => setSender(e.target.value)} placeholder={status.sender || "invoices@yourdomain.com"} className="mt-2 bg-transparent" data-testid="email-sender"/>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3">
          {!status.env_locked && (
            <Button onClick={save} disabled={busy} className="btn-pill bg-primary text-primary-foreground h-10" data-testid="email-save">
              {busy ? "Saving…" : "Save Email Settings"}
            </Button>
          )}
          <div className="flex items-end gap-2">
            <div>
              <Label className="overline">Send a test to</Label>
              <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className="mt-2 bg-transparent h-10 w-56" data-testid="email-test-to"/>
            </div>
            <Button variant="outline" onClick={sendTest} disabled={busy} className="btn-pill h-10" data-testid="email-test-send">
              <Send size={14} className="mr-1.5"/> Send Test
            </Button>
          </div>
        </div>

        {lastError && (
          <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3" data-testid="email-test-error">
            <div className="text-xs uppercase tracking-wider text-destructive mb-1">Resend said</div>
            <div className="text-sm break-words">{lastError}</div>
            {/domain|verif/i.test(lastError) && (
              <div className="text-xs text-muted-foreground mt-2">
                Add the DNS records Resend shows under Domains → your domain, at your registrar. Until then you can
                set the sender to <span className="font-medium">onboarding@resend.dev</span>, which only delivers to
                the email address that owns the Resend account.
              </div>
            )}
          </div>
        )}

        <div className="mt-7 pt-6 border-t border-border" data-testid="nudge-settings">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="font-medium text-sm">Abandoned order reminders</div>
              <p className="text-xs text-muted-foreground mt-1 max-w-lg">
                One friendly email to buyers whose payment did not go through, with a button to finish
                paying and a WhatsApp fallback. Sent once per order.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Send after</span>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={nudge.nudge_after_hours}
                  onChange={(e) => setNudge({ ...nudge, nudge_after_hours: e.target.value })}
                  onBlur={() => saveNudge({})}
                  className="w-20 h-9 bg-transparent"
                  data-testid="nudge-hours"
                />
                <span className="text-xs text-muted-foreground">hours</span>
              </div>
              <Switch
                checked={nudge.nudge_enabled}
                onCheckedChange={(v) => saveNudge({ nudge_enabled: v })}
                data-testid="nudge-switch"
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EmailSettingsCard;
